import { filterTimeline } from '../profiles/timelineItems';
import type { TimelineFilter, TimelineItem } from '../profiles/timelineItems';

/**
 * The deal drawer's top-level tabs.
 *
 * Kyu, 11 September, drawing an arrow from the Activity filter row to the
 * top of the drawer: "move these to the top. When you click any of those
 * the whole Top page opens up to whatever button you pick." And: the deal's
 * own fields "will show by default, but anyone can click other buttons to
 * switch view."
 *
 * So the filter chips that sat at the BOTTOM of a long scroll become the
 * drawer's navigation. Nothing was added to the drawer and nothing was
 * removed — the same blocks are now reachable in one tap instead of after
 * a scroll past everything else.
 *
 * ── Two names, one tab ─────────────────────────────────────────────────────
 *
 * Tasks and Orders each existed TWICE: a section with the live list and the
 * "+ Add task" / "Create CG order" controls, and an activity filter of the
 * same name showing what has already happened. Two tabs with one name would
 * be a coin toss every time. So each of those tabs shows both: the live list
 * on top, the history under it. Notes works the same way — the comment
 * thread on top, note activity below.
 *
 * ── Why "Activity" and not "All" ───────────────────────────────────────────
 *
 * The chip row said "All" because it filtered a list that was already on
 * screen. As a tab it names a destination, and "Activity" is what that
 * destination is. It also buys the row enough width to avoid scrolling
 * sideways on a laptop.
 */

export type DealTab = 'activity' | 'emails' | 'touches' | 'notes' | 'tasks' | 'orders' | 'changes';

export interface DealTabSpec {
  key: DealTab;
  label: string;
  /** Which slice of the merged timeline this tab prints, if any. */
  filter: TimelineFilter | null;
}

/**
 * Details first, because it is what opens. Then the whole feed, then the
 * feed cut by kind, in the order the old chip row used — except Changes,
 * which moves last: it is the audit trail, read least often.
 */
export const DEAL_TABS: readonly DealTabSpec[] = [
  { key: 'activity', label: 'Activity', filter: 'all' },
  { key: 'emails', label: 'Emails', filter: 'emails' },
  { key: 'touches', label: 'Calls', filter: 'touches' },
  { key: 'notes', label: 'Notes', filter: 'notes' },
  { key: 'tasks', label: 'Tasks', filter: 'tasks' },
  { key: 'orders', label: 'Orders', filter: 'orders' },
  { key: 'changes', label: 'Changes', filter: 'changes' },
];

export interface DealTabInputs {
  timeline: readonly TimelineItem[];
}

/**
 * The number beside each tab: how many entries that tab prints.
 *
 * An empty feed has a real zero and printing it is the point — "0" says the
 * tab was read and holds nothing, where a blank says nothing at all.
 *
 * ── Tasks and Orders used to count something else ─────────────────────────
 *
 * They counted OPEN tasks and LIVE order links, because the tab showed those
 * lists first and the history under them. The relayout moved both lists to
 * the associations column, so what is left on each tab is the history, and a
 * count of open tasks over a list of finished ones would be a number that
 * quietly describes a different thing than the list beneath it.
 *
 * Nobody would read that as wrong. A count beside a label gets believed, which
 * is exactly why it moves with the content rather than staying where it was.
 * The open count still exists — it is on the Tasks card in the associations
 * column, next to the open list it describes.
 */
export function dealTabCounts(input: DealTabInputs): Record<DealTab, number | null> {
  const ofFilter = (filter: TimelineFilter) => filterTimeline(input.timeline, filter).length;
  return {
    activity: input.timeline.length,
    emails: ofFilter('emails'),
    touches: ofFilter('touches'),
    notes: ofFilter('notes'),
    tasks: ofFilter('tasks'),
    orders: ofFilter('orders'),
    changes: ofFilter('changes'),
  };
}

/** The timeline slice a tab prints, or null for a tab that prints none. */
export function tabFilter(tab: DealTab): TimelineFilter | null {
  return DEAL_TABS.find((spec) => spec.key === tab)?.filter ?? null;
}

/**
 * The tab a deal drawer opens on.
 *
 * Activity, unless the drawer was opened to show one comment. The thread only
 * renders on the Notes tab, so a highlight aimed at a comment on any other tab
 * would scroll to nothing and the person would still be hunting, which is the
 * report this exists for (Ced, 21 September).
 */
export function initialDealTab(highlightCommentId: string | null | undefined): DealTab {
  return typeof highlightCommentId === 'string' && highlightCommentId !== '' ? 'notes' : 'activity';
}
